/**
 * The one path through the app, as a state machine: import → disclose →
 * reconstruct → ledger. Not a router; the URL never changes.
 *
 * What persists (localStorage, small and content-free): the phase, the run
 * parameters, and the disclosure acceptance. What never persists: the model
 * key and the chosen file, which live in memory for the session only. The
 * ledger itself lives in IndexedDB (`runs`), being content.
 */
import { create } from 'zustand'

export type Phase = 'import' | 'disclose' | 'reconstruct' | 'ledger'

export type RunParams = {
  /** The addresses the user confirmed as their own. The first is canonical. */
  readonly me: readonly string[]
  /** YYYY-MM-DD */
  readonly cutoff: string
  readonly window_days: number
  readonly model: string | null
}

export type Acceptance = {
  readonly version: string
  readonly at: string
}

/** The part of the state that survives a refresh. Never the key, never the file. */
export type PersistedState = {
  readonly phase: Phase
  readonly params: RunParams | null
  readonly acceptance: Acceptance | null
  /** The id of the completed run shown in the ledger phase. */
  readonly run_id: string | null
}

/** Session-only: cleared by a refresh on purpose. */
export type SessionState = {
  readonly file: Blob | null
  readonly api_key: string | null
  /** Study mode: scoring controls on every card and an export. Read once at boot. */
  readonly study: boolean
}

export type AppState = PersistedState &
  SessionState & {
    readonly hydrated: boolean
    hydrate(): void
    setStudy(on: boolean): void
    setImport(input: { params: RunParams; file: Blob; api_key: string }): void
    accept(acceptance: Acceptance): void
    /** After a refresh mid-run the file and key are gone; this puts them back. */
    resume(input: { file: Blob; api_key: string }): void
    completed(run_id: string): void
    runAgain(): void
    reset(): void
  }

export const STORAGE_KEY = 'cordelia.app.v1'

export const DEFAULT_WINDOW_DAYS = 90

const EMPTY: PersistedState = { phase: 'import', params: null, acceptance: null, run_id: null }

export function today(now = new Date()): string {
  return now.toISOString().slice(0, 10)
}

function isPhase(value: unknown): value is Phase {
  return value === 'import' || value === 'disclose' || value === 'reconstruct' || value === 'ledger'
}

/** Reads the persisted state back defensively; anything malformed starts over. */
export function readPersisted(raw: string | null): PersistedState {
  if (raw === null) return EMPTY
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedState>
    if (!isPhase(parsed.phase)) return EMPTY
    const params =
      parsed.params &&
      Array.isArray(parsed.params.me) &&
      typeof parsed.params.cutoff === 'string' &&
      typeof parsed.params.window_days === 'number'
        ? {
            me: parsed.params.me.filter((a): a is string => typeof a === 'string'),
            cutoff: parsed.params.cutoff,
            window_days: parsed.params.window_days,
            model: typeof parsed.params.model === 'string' ? parsed.params.model : null,
          }
        : null
    const acceptance =
      parsed.acceptance &&
      typeof parsed.acceptance.version === 'string' &&
      typeof parsed.acceptance.at === 'string'
        ? { version: parsed.acceptance.version, at: parsed.acceptance.at }
        : null
    const run_id = typeof parsed.run_id === 'string' ? parsed.run_id : null
    // A phase that needs something we no longer have falls back to the last
    // phase that stands on its own.
    let phase = parsed.phase
    if (phase === 'ledger' && run_id === null) phase = 'import'
    if ((phase === 'reconstruct' || phase === 'disclose') && params === null) phase = 'import'
    return { phase, params, acceptance, run_id }
  } catch {
    return EMPTY
  }
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function persist(state: PersistedState): void {
  const s = storage()
  if (!s) return
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Storage full or blocked: the session still works; a refresh starts over.
  }
}

const pick = (state: AppState): PersistedState => ({
  phase: state.phase,
  params: state.params,
  acceptance: state.acceptance,
  run_id: state.run_id,
})

export const useAppState = create<AppState>((set, get) => ({
  ...EMPTY,
  file: null,
  api_key: null,
  study: false,
  hydrated: false,

  setStudy(on) {
    set({ study: on })
  },

  hydrate() {
    const restored = readPersisted(storage()?.getItem(STORAGE_KEY) ?? null)
    set({ ...restored, hydrated: true })
  },

  setImport({ params, file, api_key }) {
    // Disclosure is accepted once per version, not once per run.
    const phase: Phase = get().acceptance ? 'reconstruct' : 'disclose'
    set({ params, file, api_key, phase })
    persist(pick(get()))
  },

  accept(acceptance) {
    set({ acceptance, phase: 'reconstruct' })
    persist(pick(get()))
  },

  resume({ file, api_key }) {
    set({ file, api_key })
  },

  completed(run_id) {
    // The key has done its work; drop it from memory as soon as the run ends.
    set({ run_id, phase: 'ledger', api_key: null, file: null })
    persist(pick(get()))
  },

  runAgain() {
    set({ phase: 'import', api_key: null, file: null })
    persist(pick(get()))
  },

  reset() {
    set({ ...EMPTY, api_key: null, file: null })
    storage()?.removeItem(STORAGE_KEY)
  },
}))
